import { AxiosError } from 'axios'
import {
  useQueryClient,
  useMutation as useMutationHook,
  UseMutationOptions as UseMutationBaseOptions,
  QueryKey,
} from '@tanstack/react-query'
import useAuthentication from '@/hooks/auth/useAuthentication'

export type UseMutationOptions<
  TData,
  TError = unknown,
  TVariables = void,
  TOnMutateResult = unknown,
> = UseMutationBaseOptions<TData, TError, TVariables, TOnMutateResult> & {
  sideEffectQueryKeys?: (QueryKey | undefined)[]
}

export default function useMutation<
  TData,
  TError = unknown,
  TVariables = void,
  TOnMutateResult = unknown,
>({
  sideEffectQueryKeys = [],
  onError,
  onSettled,
  ...options
}: UseMutationOptions<TData, TError, TVariables, TOnMutateResult | undefined>) {
  const { getFreshIdToken } = useAuthentication()
  const queryClient = useQueryClient()

  function cancelSideEffectQueries() {
    return Promise.all(
      sideEffectQueryKeys.map(async (key) => {
        if (!key) return
        queryClient.cancelQueries({ queryKey: key })
      }),
    )
  }

  async function invalidateSideEffectQueries() {
    // Cancel queries first so we don't clobber any optimistic updates we might make in hooks using this hook
    await cancelSideEffectQueries()
    sideEffectQueryKeys.forEach((key) => {
      if (!key) return
      queryClient.invalidateQueries({ queryKey: key })
    })
  }

  const mutation = useMutationHook<
    TData,
    TError,
    TVariables,
    TOnMutateResult | undefined
  >({
    ...options,
    onMutate: async (...args) => {
      // Cancel any ongoing side effect queries being fetched, since we're about to change them anyway
      await cancelSideEffectQueries()
      return options.onMutate?.(...args)
    },
    onError: (error, ...rest) => {
      if ((error as AxiosError)?.response?.status === 401) {
        getFreshIdToken()?.catch(() => {})
      }
      onError?.(error, ...rest)
    },
    onSettled: (...args) => {
      // Only invalidate side effect queries if there are no other mutations with the same key in flight
      // If mutationKey is undefined, it counts any other mutations without a mutationKey
      invalidateSideEffectQueries()

      onSettled?.(...args)
    },
  })

  return mutation
}
